/**
 * Shared option lists + question copy for the unified "Tune my plan"
 * walkthrough at /profile-setup. Each attribute defines:
 *
 *   - field:       the PropertyRow column key
 *   - label:       header text shown on the card ("Foundation")
 *   - question:    one-liner asking for the input ("What's the foundation type?")
 *   - help:        "why we ask" body copy that justifies the click
 *   - options:     chip choices the user can tap. Description explains
 *                  the option in one short line.
 *   - icon:        lucide icon name (resolved via rule-icon)
 *
 * Adding a new attribute card: append an entry to PROFILE_ATTRIBUTE_CARDS
 * with a matching column in PropertyRow. The walkthrough picks up
 * blank-attribute cards automatically.
 */

import type {
  Basement,
  ExteriorMaterial,
  Foundation,
  Garage,
  HomeType,
  HvacType,
  LotBand,
  PropertyRow,
  RoofMaterial,
  Sewer,
  WaterSource,
} from '@/db/types';

export interface AttributeOption<V extends string> {
  value: V;
  label: string;
  description: string;
}

export interface AttributeCard {
  field: keyof PropertyRow;
  label: string;
  question: string;
  help: string;
  /** Lucide icon name. Rendered via resolveIcon so the icon row uses the
   *  same visual vocabulary as the system cards. */
  iconName: string;
  options: AttributeOption<string>[];
  /** Wave 59: real homes often mix materials (brick front + vinyl
   *  sides, main asphalt + flat porch roof). Multi cards render toggle
   *  chips + a Save button instead of tap-to-advance. The first pick
   *  is written to `field` (the legacy single-value column) and the
   *  full array to `multiField` (the canonical JSON column). */
  multi?: boolean;
  multiField?: keyof PropertyRow;
}

/**
 * Input cards — free-text or numeric inputs that can't be answered with
 * a chip pick. These slot into the same /profile-setup queue as the
 * chip-attribute cards so users never have to leave the walkthrough to
 * type a value the catalog can't enumerate (Home nickname, build year,
 * etc.). For the address card, we use AddressAutocomplete which writes
 * address + lat + lon + zip in one shot.
 */
export type InputKind = 'text' | 'number' | 'address' | 'year';

export interface InputCard {
  field: keyof PropertyRow;
  label: string;
  question: string;
  help: string;
  iconName: string;
  kind: InputKind;
  placeholder: string;
  /** Optional numeric bounds so we can guide year/sqft entry without
   *  letting a typo turn into "year 19999." */
  min?: number;
  max?: number;
  /** Default-suggest pulled from another property field at runtime
   *  (e.g. "use last year built as the install year"). Not used in
   *  property profile cards today, kept for future extensions. */
  defaultFrom?: keyof PropertyRow;
}

export const INPUT_ATTRIBUTE_CARDS: InputCard[] = [
  {
    field: 'name',
    label: 'Home nickname',
    question: 'What do you call this home?',
    help: 'Just for you — shows up in the sidebar so multi-property setups stay clear.',
    iconName: 'Home',
    kind: 'text',
    placeholder: 'The lake place, Main Street, etc.',
  },
  {
    field: 'address',
    label: 'Street address',
    question: "What's the street address?",
    help: 'Picks your climate zone and unlocks the map preview. Pick a Places suggestion to auto-fill ZIP.',
    iconName: 'MapPin',
    kind: 'address',
    placeholder: '123 Main St, Anytown, MI',
  },
  {
    field: 'zip',
    label: 'ZIP code',
    question: "What's your ZIP code?",
    help: 'Just the 5-digit ZIP — used to set climate zone if no street address is provided.',
    iconName: 'MapPin',
    kind: 'number',
    placeholder: '12345',
    min: 501,
    max: 99999,
  },
  {
    field: 'year_built',
    label: 'Build year',
    question: 'What year was the home built?',
    help: 'Drives caulking, sealing, and aging-systems checks. Approximate is fine.',
    iconName: 'Sparkles',
    // 'year' kind renders the YearPicker scroll list instead of a text
    // input. Caller doesn't need to maintain the upper bound — the
    // picker recomputes `currentYear + 1` at render time, so the
    // selectable range advances itself every January.
    kind: 'year',
    placeholder: '1995',
    min: 1700,
  },
  {
    field: 'sqft',
    label: 'Square footage',
    question: "What's the heated square footage?",
    help: 'Used to size filter swaps, paint estimates, and lot-load math.',
    iconName: 'Ruler',
    kind: 'number',
    placeholder: '2400',
    min: 100,
    max: 30000,
  },
  {
    field: 'stories',
    label: 'Stories above ground',
    question: 'How many stories above ground?',
    help: 'Affects gutter / window / siding effort estimates.',
    iconName: 'Layers',
    kind: 'number',
    placeholder: '2',
    min: 1,
    max: 6,
  },
  {
    field: 'bedrooms',
    label: 'Bedrooms',
    question: 'How many bedrooms?',
    help: 'Used to suggest smoke / CO detector counts and filter-swap pacing.',
    iconName: 'Bed',
    kind: 'number',
    placeholder: '3',
    min: 0,
    max: 12,
  },
  {
    field: 'bathrooms',
    label: 'Bathrooms',
    question: 'How many bathrooms?',
    help: 'Tunes caulking, fan-cleaning, and plumbing inspection cadence.',
    iconName: 'Bath',
    kind: 'number',
    placeholder: '2',
    min: 0,
    max: 12,
  },
  {
    field: 'owner_first_name',
    label: 'Your first name',
    question: 'What should we call you?',
    help: 'So your timeline and emails feel like yours, not a generic robot.',
    iconName: 'PersonStanding',
    kind: 'text',
    placeholder: 'First name',
  },
];

// Basement also stands in for foundation in the Tune walkthrough — the
// two questions overlapped so much (basement=crawl ↔ foundation=crawl,
// basement=finished/unfinished/partial ↔ foundation=basement,
// basement=none ↔ foundation=slab) that asking both felt redundant.
// We dropped the foundation Tune card; profile-setup.tsx now derives
// foundation from basement on save. Pier-foundation users (rare —
// rural / waterfront) can still set it explicitly via Edit Home →
// Structure section.
const BASEMENT_OPTIONS: AttributeOption<Basement>[] = [
  { value: 'finished', label: 'Finished basement', description: 'Living space below grade — affects humidity & sump tasks' },
  { value: 'unfinished', label: 'Unfinished basement', description: 'Storage / utilities — moisture checks added' },
  { value: 'partial', label: 'Partial basement', description: 'Mix of basement + slab / crawl' },
  { value: 'crawl', label: 'Crawl space', description: 'Vented or sealed crawl below the floor' },
  { value: 'none', label: 'None / slab', description: 'Slab on grade — no basement layer' },
];

const FOUNDATION_OPTIONS: AttributeOption<Foundation>[] = [
  { value: 'slab', label: 'Slab on grade', description: 'Concrete pad — no crawl or basement below' },
  { value: 'crawl', label: 'Crawl space', description: 'Short space between floor and ground' },
  { value: 'basement', label: 'Basement', description: 'Full-height foundation walls' },
  { value: 'pier', label: 'Pier / post', description: 'Discrete pier supports — common on rural / waterfront' },
  { value: 'unsure', label: 'Not sure', description: "We'll use sensible defaults" },
];

const ROOF_OPTIONS: AttributeOption<RoofMaterial>[] = [
  { value: 'asphalt', label: 'Asphalt shingles', description: 'Most common — replace ~20-30 yr' },
  { value: 'metal', label: 'Metal', description: 'Standing seam or panels — 40-70 yr lifespan' },
  { value: 'tile', label: 'Tile', description: 'Clay or concrete tile — 50+ yr' },
  { value: 'slate', label: 'Slate', description: 'Stone slabs — century-long lifespan' },
  { value: 'wood', label: 'Wood shake', description: 'Cedar shingle / shake — needs more upkeep' },
  { value: 'flat', label: 'Flat / membrane', description: 'EPDM, TPO, modified bitumen — flat roofs' },
  { value: 'unsure', label: 'Not sure', description: "We'll use general roof tasks" },
];

const EXTERIOR_OPTIONS: AttributeOption<ExteriorMaterial>[] = [
  { value: 'vinyl', label: 'Vinyl siding', description: 'Low maintenance — clean yearly' },
  { value: 'brick', label: 'Brick', description: 'Masonry — repoint mortar over time' },
  { value: 'wood', label: 'Wood siding', description: 'Stain or paint every 3-7 yr' },
  { value: 'stucco', label: 'Stucco', description: 'Inspect for cracks; repaint ~10 yr' },
  { value: 'fiber-cement', label: 'Fiber cement', description: 'Hardie-style — durable, paint cycle ~12 yr' },
  { value: 'stone', label: 'Stone', description: 'Natural stone or veneer' },
  { value: 'aluminum', label: 'Aluminum', description: 'Older homes — paint or replace' },
  { value: 'log', label: 'Log', description: 'Stain and caulk on a regular cycle' },
  { value: 'other', label: 'Other / mixed', description: 'A mix or something not listed' },
];

const GARAGE_OPTIONS: AttributeOption<Garage>[] = [
  { value: '1-car', label: '1-car attached', description: 'One bay, attached to the home' },
  { value: '2-car', label: '2-car attached', description: 'Two bays, attached' },
  { value: '3-car', label: '3+ car', description: 'Three or more bays' },
  { value: 'detached', label: 'Detached', description: 'Separate structure — own roof + walls' },
  { value: 'carport', label: 'Carport', description: 'Open-sided shelter' },
  { value: 'none', label: 'No garage', description: 'Driveway parking only' },
];

const WATER_OPTIONS: AttributeOption<WaterSource>[] = [
  { value: 'city', label: 'City water', description: 'Municipal supply' },
  { value: 'well', label: 'Well', description: 'Private well — adds annual testing' },
  { value: 'mixed', label: 'Mixed', description: 'City + well or split source' },
  { value: 'unsure', label: 'Not sure', description: "We'll skip well-specific tasks" },
];

const SEWER_OPTIONS: AttributeOption<Sewer>[] = [
  { value: 'city', label: 'City sewer', description: 'Municipal sewer line' },
  { value: 'septic', label: 'Septic', description: 'Private system — adds pumping reminders' },
  { value: 'shared', label: 'Shared / community', description: 'HOA or neighborhood treatment' },
  { value: 'unsure', label: 'Not sure', description: "We'll skip septic-specific tasks" },
];

/** Heating fuel options. Mirrors `HeatingFuel` in the onboarding store
 *  + the `heating_fuel` column in the property row. Drives gas / oil /
 *  propane / dual-fuel-specific tasks (tank inspections, fuel-delivery
 *  reminders, gas-shutoff drills, etc). */
const HEATING_FUEL_OPTIONS: AttributeOption<
  'gas' | 'electric' | 'oil' | 'propane' | 'dual-fuel' | 'unsure'
>[] = [
  { value: 'gas', label: 'Natural gas', description: 'Piped in from the utility — most common in US towns.' },
  { value: 'electric', label: 'Electric', description: 'Heat pump, baseboards, or resistance — no combustion at all.' },
  { value: 'oil', label: 'Oil', description: 'Above- or below-ground tank refilled by delivery — common in the NE.' },
  { value: 'propane', label: 'Propane', description: 'LPG tank refilled by delivery — common rural.' },
  { value: 'dual-fuel', label: 'Dual fuel', description: 'Two sources working together (e.g. heat pump + gas backup).' },
  { value: 'unsure', label: 'Not sure', description: "We'll use general heating tasks." },
];

const HVAC_OPTIONS: AttributeOption<HvacType>[] = [
  { value: 'furnace', label: 'Forced-air furnace + AC', description: 'Most common in US homes' },
  { value: 'boiler', label: 'Boiler + radiators', description: 'Hot water heat — common in old homes' },
  { value: 'heatpump', label: 'Heat pump', description: 'Single system, heats + cools' },
  { value: 'mini', label: 'Mini-split', description: 'Ductless heads, room by room' },
  { value: 'baseboard', label: 'Electric baseboard', description: 'Resistance heaters along walls' },
  { value: 'radiant', label: 'Radiant floor', description: 'Heated floors via hydronic or electric mats' },
  { value: 'unsure', label: 'Not sure', description: "We'll use generic heating/cooling tasks" },
];

const HOME_TYPE_OPTIONS: AttributeOption<HomeType>[] = [
  { value: 'house', label: 'Single-family house', description: 'Detached, with its own land' },
  { value: 'town', label: 'Townhouse / row home', description: 'Shared walls, some yard' },
  { value: 'condo', label: 'Condo or apartment', description: 'Interior-only responsibility' },
  { value: 'mobile', label: 'Mobile / manufactured', description: 'Specialized systems' },
];

const LOT_OPTIONS: AttributeOption<LotBand>[] = [
  { value: 'q', label: 'Under ¼ acre', description: 'Small / urban lot' },
  { value: '1', label: '¼ to 1 acre', description: 'Typical suburban' },
  { value: '5', label: '1 to 5 acres', description: 'Larger / semi-rural' },
  { value: '20', label: '5 to 20 acres', description: 'Hobby farm scale' },
  { value: '20+', label: 'Over 20 acres', description: 'Acreage / rural property' },
];

/**
 * Attribute cards we show in the walkthrough. Order matters — earlier
 * cards are surfaced first when multiple are blank. Lead with the
 * high-leverage structural fields (basement, foundation) and end with
 * the softer ones (lot size, garage).
 */
export const PROFILE_ATTRIBUTE_CARDS: AttributeCard[] = [
  {
    field: 'home_type',
    label: 'Home type',
    question: 'What kind of home is it?',
    help: 'Sets the baseline outdoor task list — a condo skips lawn / gutters while a house adds them.',
    iconName: 'Home',
    options: HOME_TYPE_OPTIONS,
  },
  {
    field: 'basement',
    label: 'Basement',
    question: "What's below your main floor?",
    help: 'Drives humidity, sump, and crawl-walk reminders.',
    iconName: 'Layers',
    options: BASEMENT_OPTIONS,
  },
  // Foundation card removed in Wave 13 — it overlapped basement so
  // closely that users called the two cards "redundant." Foundation is
  // now auto-derived from basement on save (commitOnly in
  // profile-setup.tsx). Pier-foundation users can still pick it
  // explicitly via Edit Home → Structure section.
  {
    field: 'roof_material',
    label: 'Roof material',
    question: "What's your roof made of?",
    help: 'Replacement and inspection cadence vary a LOT by material. Main + porch roof can differ — pick every material up there.',
    iconName: 'Home',
    options: ROOF_OPTIONS,
    multi: true,
    multiField: 'roof_materials_json',
  },
  // Wave 43 restored the Exterior card; Wave 59 made it a true
  // multi-select (brick front + vinyl sides is the NORM, not the
  // exception) — the first pick lands in exterior_material as the
  // primary and the whole set in exterior_materials_json, same
  // storage contract as Edit Home → Structure.
  {
    field: 'exterior_material',
    label: 'Exterior',
    question: "What's on the exterior?",
    help: 'Drives siding-specific tasks like caulk, paint, and seal cycles. Mixed home? Pick every material on it.',
    iconName: 'Layers',
    options: EXTERIOR_OPTIONS,
    multi: true,
    multiField: 'exterior_materials_json',
  },
  {
    field: 'hvac_type',
    label: 'Primary HVAC',
    question: 'How is the home heated and cooled?',
    help: 'Picks the right filter, service, and seasonal change-over tasks.',
    iconName: 'Thermometer',
    options: HVAC_OPTIONS,
  },
  {
    // Paired with HVAC right after — different concern (the FUEL, not
    // the equipment) so it deserves its own card. Drives oil-tank /
    // propane / gas-line tasks that the equipment type alone can't
    // disambiguate.
    field: 'heating_fuel',
    label: 'Heating fuel',
    question: "What does the heat run on?",
    help: 'Drives oil-tank, propane delivery, or gas-shutoff tasks where relevant.',
    iconName: 'Flame',
    options: HEATING_FUEL_OPTIONS,
  },
  {
    field: 'water_source',
    label: 'Water source',
    question: 'Where does your water come from?',
    help: 'Wells add annual testing; city water skips those.',
    iconName: 'Droplet',
    options: WATER_OPTIONS,
  },
  {
    field: 'sewer',
    label: 'Sewer / septic',
    question: 'How does waste leave the home?',
    help: 'Septic adds 3-year pumping reminders; city sewer skips them.',
    iconName: 'Waves',
    options: SEWER_OPTIONS,
  },
  {
    field: 'garage',
    label: 'Garage',
    question: 'What garage situation?',
    help: 'Turns on garage-door + garage-storage maintenance.',
    iconName: 'Warehouse',
    options: GARAGE_OPTIONS,
  },
  {
    field: 'lot_band',
    label: 'Lot size',
    question: 'How much land are you managing?',
    help: 'Scales outdoor effort estimates — mowing, leaf load, etc.',
    iconName: 'Trees',
    options: LOT_OPTIONS,
  },
];
